import type { NextPage, GetStaticProps } from 'next';
import { useScreenViewEvent } from '@stitch-fix/event-reporter';
import { BreadcrumbsProps } from '../../../../components/Breadcrumbs';
import CategorySetPageCategories from '../../../../components/CategorySetPageCategories';
import Layout from '../../../../components/Layout';
import PageHeader from '../../../../components/PageHeader';
import { getCategorySetPageByBusinessLine } from '../../../../queries/getCategorySetPage';
import { CategorySetPageFragment } from '../../../../queries/getCategorySetPageAtBuildTime';
import { BusinessLineRoutes, routeToBusinessLine, routeToBusinessLineDisplay } from '../../../../utils/routingHelper';
import { CATEGORY_SET_IDS } from './constants';
export const getStaticPaths = async () => {
  // We currently have to manually define published category sets because we
  // don't have a field that will return a dynamic list of them.
  const paths = CATEGORY_SET_IDS.map(categorySetId => [{
    params: {
      businessLine: 'women',
      categorySetId
    }
  }, {
    params: {
      businessLine: 'men',
      categorySetId
    }
  }]).flat();

  // Setting `fallback` to false to properly 404 on any categories not statically generated
  return {
    paths,
    fallback: false
  };
};
interface PageProps {
  breadcrumbs: BreadcrumbsProps['breadcrumbs'];
  businessLine: BusinessLineRoutes;
  categorySetPage: CategorySetPageFragment | null;
}
export const getStaticProps: GetStaticProps<PageProps, {
  businessLine: BusinessLineRoutes;
  categorySetId: string;
}> = async context => {
  // freestyle isn't available in the UK
  if (context.locale === 'en-GB') {
    return {
      notFound: true
    };
  }
  const {
    businessLine = 'women',
    categorySetId = ''
  } = context.params || {};
  const categorySetPage = await getCategorySetPageByBusinessLine(routeToBusinessLine[businessLine], categorySetId);
  const breadcrumbs: BreadcrumbsProps['breadcrumbs'] = [{
    text: routeToBusinessLineDisplay(businessLine),
    href: `/${businessLine}`
  }, {
    text: categorySetPage?.title || ''
  }];
  return {
    props: {
      breadcrumbs,
      businessLine,
      categorySetPage
    }
  };
};
const CategorySetPage: NextPage<PageProps> = ({
  breadcrumbs,
  businessLine,
  categorySetPage
}) => {
  // v2 page view event
  const {
    EventProvider
  } = useScreenViewEvent({
    schema: 'category_set_screen_view',
    category_set_id: categorySetPage?.categorySet.id || '',
    name: 'category_set',
    source_app: 'categories-next-ui'
  });
  return <EventProvider>
      <Layout title={categorySetPage?.title} breadcrumbs={breadcrumbs} hero={{
      businessLine,
      variant: 'categorySetPage'
    }}>
        <PageHeader title={categorySetPage?.title || ''} />

        {categorySetPage && <CategorySetPageCategories businessLine={businessLine} categorySetPage={categorySetPage} />}
      </Layout>
    </EventProvider>;
};
export default CategorySetPage;